import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import httpClient, { extractApiError } from "../api/httpClient";
import { useAuth } from "./AuthContext";
import { useUI } from "./UIContext";

const defaultBranding = {
  id: null,
  aziendaId: null,
  logoUrl: "",
  layout: "SIDEBAR",
  theme: "LIGHT",
  colorPrimary: "#3498db",
  colorSecondary: "#6c757d",
  colorAccent: "#e67e22",
  lightTextColor: "#1f2937",
  lightForegroundColor: "#ffffff",
  lightFieldColor: "#f8fafc",
  lightTableCellTextColor: "#3498db",
  darkTextColor: "#e2e8f0",
  darkForegroundColor: "#111b2e",
  darkFieldColor: "#1f2937",
  darkTableCellTextColor: "#7dd3fc",
  fontFamily: "'Manrope', 'IBM Plex Sans', 'Segoe UI', sans-serif",
  appFontSize: "16px",
  headerTitle: "ShiftFlow SaaS",
  faviconUrl: "",
  sidebarEnabled: true,
  darkModeToggleEnabled: true,
  customCss: "",
  tableFontFamily: "",
  tableFontSize: "0.875rem",
  tableHeaderFontSize: "0.82rem",
  contentMaxWidth: "100%",
  contentPaddingX: "24px",
  contentPaddingY: "24px",
  pageSurfaceRadius: "14px",
  pageShellGradientStart: "#d6eaf8",
  pageShellGradientMiddle: "#f1f5f9",
  pageShellGradientEnd: "#ffffff",
  pageShellGradient: "linear-gradient(180deg, #d6eaf8 0%, #f1f5f9 26%, #ffffff 100%)",
};

const BrandingContext = createContext(null);

const isValidHexColor = (value) => /^#[0-9A-Fa-f]{6}$/.test(String(value || ""));

export const resolveBrandingThemeTokens = (brandingConfig = {}, mode = "light") => {
  const isDark = String(mode || "light").toLowerCase() === "dark";

  return {
    textColor: isDark
      ? brandingConfig.darkTextColor || defaultBranding.darkTextColor
      : brandingConfig.lightTextColor || defaultBranding.lightTextColor,
    foregroundColor: isDark
      ? brandingConfig.darkForegroundColor || defaultBranding.darkForegroundColor
      : brandingConfig.lightForegroundColor || defaultBranding.lightForegroundColor,
    fieldColor: isDark
      ? brandingConfig.darkFieldColor || defaultBranding.darkFieldColor
      : brandingConfig.lightFieldColor || defaultBranding.lightFieldColor,
    tableCellTextColor: isDark
      ? brandingConfig.darkTableCellTextColor || defaultBranding.darkTableCellTextColor
      : brandingConfig.lightTableCellTextColor || defaultBranding.lightTableCellTextColor,
  };
};

const extractHexStops = (gradientValue) => {
  const matches = String(gradientValue || "").match(/#[0-9A-Fa-f]{6}/g) || [];
  return {
    start: matches[0],
    middle: matches[1],
    end: matches[2],
  };
};

const buildPageShellGradient = (start, middle, end) =>
  `linear-gradient(180deg, ${start} 0%, ${middle} 26%, ${end} 100%)`;

const normalizeBranding = (branding = {}) => ({
  ...defaultBranding,
  ...branding,
  colorPrimary: isValidHexColor(branding.colorPrimary) ? branding.colorPrimary : defaultBranding.colorPrimary,
  colorSecondary: isValidHexColor(branding.colorSecondary) ? branding.colorSecondary : defaultBranding.colorSecondary,
  colorAccent: isValidHexColor(branding.colorAccent) ? branding.colorAccent : defaultBranding.colorAccent,
  lightTextColor: isValidHexColor(branding.lightTextColor) ? branding.lightTextColor : defaultBranding.lightTextColor,
  lightForegroundColor: isValidHexColor(branding.lightForegroundColor) ? branding.lightForegroundColor : defaultBranding.lightForegroundColor,
  lightFieldColor: isValidHexColor(branding.lightFieldColor) ? branding.lightFieldColor : defaultBranding.lightFieldColor,
  lightTableCellTextColor: isValidHexColor(branding.lightTableCellTextColor)
    ? branding.lightTableCellTextColor
    : defaultBranding.lightTableCellTextColor,
  darkTextColor: isValidHexColor(branding.darkTextColor) ? branding.darkTextColor : defaultBranding.darkTextColor,
  darkForegroundColor: isValidHexColor(branding.darkForegroundColor) ? branding.darkForegroundColor : defaultBranding.darkForegroundColor,
  darkFieldColor: isValidHexColor(branding.darkFieldColor) ? branding.darkFieldColor : defaultBranding.darkFieldColor,
  darkTableCellTextColor: isValidHexColor(branding.darkTableCellTextColor)
    ? branding.darkTableCellTextColor
    : defaultBranding.darkTableCellTextColor,
  fontFamily: branding.fontFamily || defaultBranding.fontFamily,
  appFontSize: branding.appFontSize || defaultBranding.appFontSize,
  tableFontFamily: branding.tableFontFamily || branding.fontFamily || defaultBranding.fontFamily,
  tableFontSize: branding.tableFontSize || defaultBranding.tableFontSize,
  tableHeaderFontSize: branding.tableHeaderFontSize || defaultBranding.tableHeaderFontSize,
  headerTitle: branding.headerTitle || defaultBranding.headerTitle,
  contentMaxWidth: branding.contentMaxWidth || defaultBranding.contentMaxWidth,
  contentPaddingX: branding.contentPaddingX || defaultBranding.contentPaddingX,
  contentPaddingY: branding.contentPaddingY || defaultBranding.contentPaddingY,
  pageSurfaceRadius: branding.pageSurfaceRadius || defaultBranding.pageSurfaceRadius,
  pageShellGradientStart:
    branding.pageShellGradientStart || extractHexStops(branding.pageShellGradient).start || defaultBranding.pageShellGradientStart,
  pageShellGradientMiddle:
    branding.pageShellGradientMiddle || extractHexStops(branding.pageShellGradient).middle || defaultBranding.pageShellGradientMiddle,
  pageShellGradientEnd:
    branding.pageShellGradientEnd || extractHexStops(branding.pageShellGradient).end || defaultBranding.pageShellGradientEnd,
  pageShellGradient: buildPageShellGradient(
    branding.pageShellGradientStart || extractHexStops(branding.pageShellGradient).start || defaultBranding.pageShellGradientStart,
    branding.pageShellGradientMiddle || extractHexStops(branding.pageShellGradient).middle || defaultBranding.pageShellGradientMiddle,
    branding.pageShellGradientEnd || extractHexStops(branding.pageShellGradient).end || defaultBranding.pageShellGradientEnd,
  ),
});

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(defaultBranding);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const { showNotification } = useUI();

  const applyDocumentMetadata = useCallback((nextBranding) => {
    if (!nextBranding) {
      return;
    }

    const title = nextBranding.headerTitle || defaultBranding.headerTitle;
    document.title = title;
    document.documentElement.style.setProperty("--app-font-family", nextBranding.fontFamily || defaultBranding.fontFamily);
    document.documentElement.style.setProperty("--app-font-size", nextBranding.appFontSize || defaultBranding.appFontSize);
    document.documentElement.style.setProperty("--app-table-font-family", nextBranding.tableFontFamily || nextBranding.fontFamily || defaultBranding.fontFamily);
    document.documentElement.style.setProperty("--app-table-font-size", nextBranding.tableFontSize || defaultBranding.tableFontSize);
    document.documentElement.style.setProperty("--app-table-header-font-size", nextBranding.tableHeaderFontSize || defaultBranding.tableHeaderFontSize);
    document.documentElement.style.setProperty("--app-table-cell-text-color", nextBranding.lightTableCellTextColor || defaultBranding.lightTableCellTextColor);
    document.documentElement.style.setProperty("--app-disponibilita-text-color", nextBranding.lightTableCellTextColor || defaultBranding.lightTableCellTextColor);
    document.documentElement.style.setProperty("--app-reperibilita-text-color", nextBranding.colorAccent || defaultBranding.colorAccent);
    document.documentElement.style.setProperty("--app-content-max-width", nextBranding.contentMaxWidth || defaultBranding.contentMaxWidth);
    document.documentElement.style.setProperty("--app-content-padding-x", nextBranding.contentPaddingX || defaultBranding.contentPaddingX);
    document.documentElement.style.setProperty("--app-content-padding-y", nextBranding.contentPaddingY || defaultBranding.contentPaddingY);
    document.documentElement.style.setProperty("--app-page-surface-radius", nextBranding.pageSurfaceRadius || defaultBranding.pageSurfaceRadius);
    document.documentElement.style.setProperty("--app-page-shell-gradient", nextBranding.pageShellGradient || defaultBranding.pageShellGradient);
    document.documentElement.style.setProperty("--app-page-shell-gradient-start", nextBranding.pageShellGradientStart || defaultBranding.pageShellGradientStart);
    document.documentElement.style.setProperty("--app-page-shell-gradient-middle", nextBranding.pageShellGradientMiddle || defaultBranding.pageShellGradientMiddle);
    document.documentElement.style.setProperty("--app-page-shell-gradient-end", nextBranding.pageShellGradientEnd || defaultBranding.pageShellGradientEnd);

    const faviconHref = nextBranding.faviconUrl || "";
    let faviconLink = document.querySelector('link[rel="icon"]');
    if (!faviconLink) {
      faviconLink = document.createElement("link");
      faviconLink.rel = "icon";
      document.head.appendChild(faviconLink);
    }

    if (faviconHref) {
      faviconLink.href = faviconHref;
    }
  }, []);

  const loadBranding = useCallback(async () => {
    if (!isAuthenticated) {
      setBranding(defaultBranding);
      return defaultBranding;
    }

    setLoading(true);
    try {
      const response = await httpClient.get("/branding");
      const nextBranding = normalizeBranding(response?.data || {});
      setBranding(nextBranding);
      applyDocumentMetadata(nextBranding);
      return nextBranding;
    } catch (error) {
      const nextBranding = defaultBranding;
      setBranding(nextBranding);
      applyDocumentMetadata(nextBranding);
      if (error?.response?.status !== 404) {
        showNotification(extractApiError(error), "warning");
      }
      return nextBranding;
    } finally {
      setLoading(false);
    }
  }, [applyDocumentMetadata, isAuthenticated, showNotification]);

  const upsertBranding = useCallback(
    async (payload) => {
      const response = await httpClient.put("/branding", payload);
      const nextBranding = normalizeBranding(response?.data || payload);
      setBranding(nextBranding);
      applyDocumentMetadata(nextBranding);
      return nextBranding;
    },
    [applyDocumentMetadata]
  );

  const resetBranding = useCallback(async () => {
    const response = await httpClient.post("/branding/reset");
    const nextBranding = normalizeBranding(response?.data || defaultBranding);
    setBranding(nextBranding);
    applyDocumentMetadata(nextBranding);
    return nextBranding;
  }, [applyDocumentMetadata]);

  useEffect(() => {
    if (!isAuthenticated) {
      setBranding(defaultBranding);
      document.title = defaultBranding.headerTitle;
      applyDocumentMetadata(defaultBranding);
      return;
    }

    loadBranding();
  }, [applyDocumentMetadata, isAuthenticated, loadBranding]);

  const value = useMemo(
    () => ({
      branding,
      loading,
      loadBranding,
      upsertBranding,
      resetBranding,
      getThemeTokens: (mode) => resolveBrandingThemeTokens(branding, mode),
      defaultBranding,
      isValidHexColor,
    }),
    [branding, loading, loadBranding, upsertBranding, resetBranding]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error("useBranding deve essere usato dentro BrandingProvider");
  }

  return context;
}